import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { ScreenHeader } from '../../components/common';
import { localdb } from '../../lib/localdb';
import type { Backend } from '../../lib/backend/types';
import { useApp } from '../../state/AppState';
import { LESSON_COUNT } from './curriculum';
import {
  ModuleContents,
  ORDERED_MODULES,
  learnDoneCount,
  nextUnfinishedLesson,
  searchLessonIds,
} from './LearnContents';

/* --- Progress ---------------------------------------------------------------

   One kv row, `learnProgress`, holding the ids of finished lessons. A lesson
   with a checkpoint is finished by answering it (right OR wrong — the
   explanation is the point, not the score); a lesson without one is finished
   by reading it and moving on. Purely local, purely deterministic, zero AI.

   Progress here RECORDS where a reader has been. It has never gated anything
   and must not start: no lesson depends on another, so a tick is a bookmark,
   not a key. That is why it renders as a quiet count and a thin bar rather
   than as this screen's call to action.

   The helpers live here rather than in a third file so LessonView (which
   writes) and HomeScreen (which reads for its "Keep learning" card) share one
   implementation. */

export const LEARN_PROGRESS_KEY = 'learnProgress';

export async function readLearnDone(): Promise<Set<string>> {
  const stored = await localdb.get<string[]>('kv', LEARN_PROGRESS_KEY).catch(() => undefined);
  return new Set(Array.isArray(stored) ? stored.filter((id) => typeof id === 'string') : []);
}

export async function markLessonDone(id: string, backend?: Backend): Promise<Set<string>> {
  const done = await readLearnDone();
  if (!done.has(id)) {
    done.add(id);
    await localdb.set('kv', LEARN_PROGRESS_KEY, [...done]).catch(() => undefined);
    // Fire-and-forget: the lesson is done the moment the local write lands.
    // The server copy exists so progress survives a reinstall or a second
    // device, not to gate anything.
    void backend?.saveLearnProgress?.([...done]).catch(() => undefined);
  }
  return done;
}

/**
 * Union local and server progress, then repair whichever side is behind.
 *
 * Union is the correct merge for "finished lesson" flags: a lesson done on
 * ANY device is done, and un-finishing is not a concept here. Signed out (or
 * demo mode, which has no server copy) resolves to the local set unchanged.
 */
export async function syncLearnProgress(backend?: Backend): Promise<Set<string>> {
  const local = await readLearnDone();
  if (!backend?.getLearnProgress) return local;
  try {
    const remote = await backend.getLearnProgress();
    if (remote === null) return local;
    const merged = new Set([...local, ...remote]);
    if (merged.size > local.size) {
      await localdb.set('kv', LEARN_PROGRESS_KEY, [...merged]).catch(() => undefined);
    }
    if (merged.size > remote.length) {
      await backend.saveLearnProgress?.([...merged]).catch(() => undefined);
    }
    return merged;
  } catch {
    return local;
  }
}

// Curriculum arithmetic moved to LearnContents (it has no storage in it), but
// HomeScreen and LessonView have always imported it from here. Re-exported so
// those call sites keep working and there is one obvious place to import from.
export {
  learnDoneCount,
  lessonAfter,
  lessonBefore,
  nextUnfinishedLesson,
  ORDERED_LESSONS,
  ORDERED_MODULES,
} from './LearnContents';

/** Thin fill bar. Uses --mode so it takes a tradition's colour under data-mode. */
function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      style={{ height: 4, borderRadius: 999, background: 'var(--mode-soft)', overflow: 'hidden' }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          borderRadius: 999,
          background: 'var(--mode)',
          transition: 'width 0.3s ease',
        }}
      />
    </div>
  );
}

/**
 * The Learn surface — a book's contents page, not a track.
 *
 * Every module is listed expanded, every lesson is a direct link, and a search
 * box finds a lesson by its title OR by any glossary term it teaches. Progress
 * is information (a count and a hairline bar); "continue" is a small secondary
 * link, because a book does not force itself open at the bookmark.
 *
 * Exempt from both app gates (see App.tsx): learning works before an account
 * or a purchase exists. Nothing on this screen costs a message — the whole
 * curriculum is deterministic local content.
 */
export function LearnScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [done, setDone] = useState<ReadonlySet<string>>(new Set<string>());
  const [query, setQuery] = useState('');
  // Folded chapters, by module id. Empty by default: everything is open until
  // the reader chooses otherwise.
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set<string>());

  useEffect(() => {
    let alive = true;
    // Pull any progress made on another device, then repair whichever side is
    // behind. Signed-out and demo sessions resolve to the local copy.
    syncLearnProgress(app.backend).then((set) => {
      if (alive) setDone(set);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.uid]);

  const doneCount = learnDoneCount(done);
  const next = nextUnfinishedLesson(done);

  // null = not searching. An empty Set = searched and nothing matched.
  const matches = useMemo(() => searchLessonIds(query), [query]);

  const groups = useMemo(
    () =>
      ORDERED_MODULES.map((module) => ({
        module,
        lessons: matches ? module.lessons.filter((l) => matches.has(l.id)) : module.lessons,
      })).filter((group) => group.lessons.length > 0),
    [matches],
  );

  const toggle = (id: string) =>
    setFolded((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(id)) nextSet.delete(id);
      else nextSet.add(id);
      return nextSet;
    });

  return (
    <div className="screen">
      <ScreenHeader title={t('learn.title')} subtitle={t('learn.subtitle')} back />

      {/* Progress as information, never as the instruction. The count, a
          hairline, and "continue" demoted to a text link beside it. */}
      <div className="card col" style={{ gap: 8, marginBottom: 12 }}>
        <div className="spread">
          <span className="small">
            {t('learn.progress', { done: doneCount, total: LESSON_COUNT })}
          </span>
          {next && (
            <Link
              to={`/learn/${next.lesson.id}`}
              className="tiny accent"
              style={{ flexShrink: 0 }}
            >
              {doneCount > 0 ? t('learn.continue') : t('learn.start')} →
            </Link>
          )}
        </div>
        <ProgressBar done={doneCount} total={LESSON_COUNT} />
        <div className="tiny faint" style={{ lineHeight: 1.5 }}>
          {next ? t('learn.anyOrder') : t('learn.allDone')}
        </div>
      </div>

      {/* The index. Title match or glossary-term match — "sade sati" finds the
          lesson that teaches it even though no lesson title says it. */}
      <input
        className="input"
        type="search"
        value={query}
        aria-label={t('learn.search')}
        placeholder={t('learn.search')}
        onChange={(e) => setQuery(e.target.value)}
      />

      {groups.length === 0 && (
        <div className="card muted small" style={{ marginTop: 12, lineHeight: 1.6 }}>
          {t('learn.searchEmpty')}
        </div>
      )}

      <div className="col" style={{ marginTop: 12 }}>
        {groups.map(({ module, lessons }) => (
          <ModuleContents
            key={module.id}
            module={module}
            lessons={lessons}
            done={done}
            // A search result is useless folded shut, so searching forces every
            // matching chapter open regardless of what the reader folded.
            open={matches !== null || !folded.has(module.id)}
            onToggle={() => toggle(module.id)}
          />
        ))}
      </div>

      <Link
        to="/glossary"
        className="small accent center"
        style={{ display: 'block', margin: '16px 0' }}
      >
        {t('glossary.seeAll')} →
      </Link>
    </div>
  );
}
